import { createSelector } from 'reselect';
import { defaults, pick } from 'lodash';

import { extractDefaultPreferences } from './utils';

const selectSession = (state) => (state && state.session) || undefined;

/*
 * Selectors to access main user settings: info, locale, token..
 */

/*
 * Access main user details such as name, email, id...
 */
const selectUserDetails = createSelector(selectSession, (session) => (session && session.userInfo) || undefined);

const selectUserToken = createSelector(selectSession, (session) => session && session.token);

const selectUserPreferences = createSelector(selectSession, (session) => session && session.preferences);

/**
 * Creates a selector that retrieves the requested preferences values
 * from the store, and fills it with defaults when it isn't defined
 * @param  {String[]} preferencesKeys - list of preferences keys
 * @return {Object}   object with all requested preferences
 */
const makeSelectUserPreferences = (preferencesKeys) =>
    createSelector(selectUserPreferences, (prefs) =>
        defaults({}, pick(prefs, preferencesKeys), extractDefaultPreferences(preferencesKeys)),
    );

/*
 * Selectors to get the list of authorised properties
 */

const selectAuthorisedProperties = createSelector(selectSession, (session) => session && session.authorisedProperties);

const selectAuthorisedPropertiesList = createSelector(selectAuthorisedProperties, (properties) =>
    properties ? Object.values(properties) : [],
);

/*
 * Selectors to get the currently selected property's details
 */

const selectSelectedPropertyId = createSelector(selectSession, (session) => session && session.selectedProperty);

const selectSelectedPropertyDetails = createSelector(
    selectSelectedPropertyId,
    selectAuthorisedProperties,
    (propertyId, properties) => {
        if (propertyId === undefined || properties === undefined) return undefined;
        return properties[propertyId];
    },
);

const selectCurrency = createSelector(selectSelectedPropertyDetails, (property) => property && property.currency);

const selectRoomCount = createSelector(selectSelectedPropertyDetails, (property) => property && property.roomCount);

const selectFinancialYearStart = createSelector(
    selectSelectedPropertyDetails,
    (property) => property && property.financialYearStart,
);

const selectAccessibleFeatures = createSelector(
    selectSelectedPropertyDetails,
    (property) => property && (property.features || []),
);

const selectVendorId = createSelector(selectSelectedPropertyDetails, (property) => property && property.vendorID);

const selectPropertyName = createSelector(selectSelectedPropertyDetails, (property) => property && property.name);

const selectIsPropertyTwoWay = createSelector(
    selectSelectedPropertyDetails,
    (property) => property && property.isTwoWayEnabled,
);

const selectPropertySegmentBuckets = createSelector(
    selectSelectedPropertyDetails,
    (property) => property && property.segmentBuckets,
);

const selectOtaHotelId = createSelector(selectSelectedPropertyDetails, (property) => property && property.otaHotelID);

/*
 * Selectors relating to the config of the currently selected property
 */

const selectPropertyConfig = createSelector(selectSelectedPropertyDetails, (property) => property && property.config);

const selectCardinalRecommendationsConfig = createSelector(selectPropertyConfig, (propertyConfig) =>
    ((propertyConfig && propertyConfig.recommendations) || []).find((row) => row.version === 1),
);

const selectGroupBlocksConfig = createSelector(
    selectPropertyConfig,
    (propertyConfig) => (propertyConfig && propertyConfig.groupBlocks) || {},
);

const selectOtaCompsets = createSelector(
    selectPropertyConfig,
    (propertyConfig) => propertyConfig?.ota?.competitorSets || {},
);

const selectDefaultRecommendationVersion = createSelector(
    selectPropertyConfig,
    (propertyConfig) => (propertyConfig && propertyConfig.defaultRecommendationVersion) || 1,
);

/*
 * Quick-access selectors to know if a user is logged / has selected a property to view
 */

const selectUserIsLogged = createSelector(selectSession, (session) => !!session);

const selectHasSelectedProperty = createSelector(selectSelectedPropertyId, (propertyId) => propertyId != null);

const selectUserConfig = createSelector(selectSession, (session) => session && session.config);

export {
    selectUserToken,
    selectUserDetails,
    selectUserPreferences,
    makeSelectUserPreferences,
    selectAuthorisedProperties,
    selectAuthorisedPropertiesList,
    selectSelectedPropertyId,
    selectCurrency,
    selectRoomCount,
    selectFinancialYearStart,
    selectAccessibleFeatures,
    selectVendorId,
    selectPropertyName,
    selectUserIsLogged,
    selectHasSelectedProperty,
    selectIsPropertyTwoWay,
    selectPropertySegmentBuckets,
    selectOtaHotelId,
    selectPropertyConfig,
    selectCardinalRecommendationsConfig,
    selectGroupBlocksConfig,
    selectOtaCompsets,
    selectUserConfig,
    selectDefaultRecommendationVersion,
};

export default {
    selectSession,
    selectAuthorisedProperties,
    selectSelectedPropertyDetails,
};
